import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/shared/query';
import { appointmentAPI } from '../api/appointment.api';
import {
  type AppointmentFormMode,
  type AppointmentFormValues,
  type AppointmentVisitKind,
} from '../utils/appointmentForm';

const subjectKey = (values: Pick<AppointmentFormValues, 'subjectType' | 'patientId' | 'leadId'>): string =>
  values.subjectType === 'patient' ? `p:${values.patientId}` : `l:${values.leadId}`;

/**
 * Prefills Lịch mới / Tái khám from completed history when booking, or when the
 * subject of an existing appointment changes. A saved classification stays put,
 * and appointments with no classification stay blank until someone chooses.
 */
export const useSuggestedVisitKind = ({
  mode,
  values,
  initialValues,
  baseline,
  baselineReady,
  excludeAppointmentId,
  setVisitKind,
  adoptSuggestedVisitKind,
}: {
  mode: AppointmentFormMode;
  values: AppointmentFormValues;
  initialValues: AppointmentFormValues;
  baseline: AppointmentFormValues;
  baselineReady: boolean;
  excludeAppointmentId?: string;
  setVisitKind: (visitKind: AppointmentVisitKind) => void;
  adoptSuggestedVisitKind: (visitKind: AppointmentVisitKind) => void;
}): { markVisitKindTouched: () => void } => {
  const currentKey = subjectKey(values);
  const savedKey = subjectKey(initialValues);
  const keepSavedChoice = mode === 'edit' && currentKey === savedKey;
  const subjectId = values.subjectType === 'patient' ? values.patientId : values.leadId;
  const touchedKey = useRef('');

  const suggestionQuery = useQuery({
    queryKey: queryKeys.appointment.visitKindSuggestion({
      patientId: values.subjectType === 'patient' ? values.patientId : undefined,
      leadId: values.subjectType === 'lead' ? values.leadId : undefined,
      excludeAppointmentId,
    }),
    queryFn: () =>
      appointmentAPI.suggestVisitKind({
        patientId: values.subjectType === 'patient' ? values.patientId : undefined,
        leadId: values.subjectType === 'lead' ? values.leadId : undefined,
        excludeAppointmentId,
      }),
    enabled: Boolean(subjectId) && !keepSavedChoice,
  });

  const suggested = suggestionQuery.data;

  useEffect(() => {
    if (touchedKey.current && touchedKey.current !== currentKey) {
      touchedKey.current = '';
    }
    if (!suggested || !subjectId || keepSavedChoice) return;
    if (touchedKey.current === currentKey) return;
    setVisitKind(suggested);
    if (
      baselineReady &&
      baseline.visitKind === '' &&
      subjectKey(baseline) === currentKey
    ) {
      adoptSuggestedVisitKind(suggested);
    }
  }, [
    suggested,
    subjectId,
    keepSavedChoice,
    currentKey,
    baselineReady,
    baseline,
    setVisitKind,
    adoptSuggestedVisitKind,
  ]);

  return {
    markVisitKindTouched: () => {
      touchedKey.current = currentKey;
    },
  };
};
